// Track B (realtime-inbox migration) FE1.3/FE1.4 — pure conversation-list
// merge logic for WS `conversation.message` events. Track B2
// (pagination-and-ws-tenant-wide) adds a second handler below,
// createApplyConversationListUpsert(), for the new tenant-wide
// `conversation_list.upsert` event, plus a small dedupe helper shared with
// InboxPage.jsx's infinite-query page cache.
// growchat-doc/realtime-inbox/spec-websocket-conversation-migration.md
// growchat-doc/realtime-inbox/spec-inbox-conversation-list-pagination.md
//
// Extracted out of src/pages/InboxPage.jsx (which must only export its
// default component — react-refresh/only-export-components) so this stays
// independently unit-testable. Takes InboxPage.jsx's own
// preview/relative-time/channel-key helpers as injected dependencies rather
// than re-implementing them, so there's exactly one definition of "what
// does a message preview look like" in the codebase — this module doesn't
// duplicate InboxPage.jsx's logic, it's parameterized by it.
//
// @param {object} deps
// @param {(latestMsg: object, t: Function) => string} deps.getConversationPreview
// @param {(iso: string, t: Function) => string} deps.relativeTime
// @param {(channel: string) => string|null} deps.normalizeChannelKey
export function createApplyWsMessageToConversationList({
  getConversationPreview,
  relativeTime,
  normalizeChannelKey,
}) {
  // Patches the unified conversation-list cache (the output of
  // toUnifiedContacts(), NOT raw API rows) so a WS `conversation.message`
  // event updates the last-message preview for the matching contact/channel
  // the instant it arrives.
  //
  // Deliberately does NOT re-sort the array — none of InboxPage.jsx's other
  // local optimistic patches via patchConversationCaches() (mark-read,
  // assignment, AI toggle, ...) re-sort either. This is safe because
  // sort/insert ordering is no longer this function's job as of Track B2
  // (pagination-and-ws-tenant-wide): every message write that reaches this
  // handler also fires a second, separate `conversation_list.upsert` event
  // on the new tenant-wide topic (see createApplyConversationListUpsert()
  // below), which DOES re-sort/insert, and InboxPage.jsx applies both
  // handlers for the same message. The comment this replaced ("list order
  // updates on the next 5s poll tick") described the pre-Phase-2 world —
  // that poll was removed in the cutover (spec-websocket-cutover-remove-
  // polling.md) and no longer exists, so it stopped being true then; the
  // tenant-wide upsert handler is what closes that gap now.
  //
  // unread is only bumped for role:"user" messages (mirrors the server's
  // own unread_count bump in ConversationRepository.append_message()) and
  // only when the message's thread isn't the one currently open — opening
  // a thread and receiving a message you're already looking at shouldn't
  // tick its own unread badge.
  return function applyWsMessageToConversationList(conversations, event, { t, activeContactId, activeChannel } = {}) {
    if (!Array.isArray(conversations) || !event) return conversations
    const contactId = event.contact_id
    const channel = normalizeChannelKey(event.channel)
    if (!contactId || !channel) return conversations

    const message = event.message || {}
    const timestamp = message.timestamp || new Date().toISOString()
    const isActiveThread = activeContactId === contactId && normalizeChannelKey(activeChannel) === channel
    const preview = getConversationPreview(message, t)

    let changed = false
    const next = conversations.map((conv) => {
      if (conv.contact_id !== contactId) return conv
      const channelMeta = Array.isArray(conv.channelMeta) ? conv.channelMeta : []
      let metaChanged = false
      const nextChannelMeta = channelMeta.map((meta) => {
        if (normalizeChannelKey(meta.channel) !== channel) return meta
        metaChanged = true
        const bumpUnread = message.role === 'user' && !isActiveThread
        return {
          ...meta,
          preview,
          updated_at: timestamp,
          unread: bumpUnread ? (Number(meta.unread) || 0) + 1 : meta.unread,
        }
      })
      if (!metaChanged) return conv
      changed = true

      const unreadByChannel = { ...conv.unreadByChannel }
      const bumpedMeta = nextChannelMeta.find((meta) => normalizeChannelKey(meta.channel) === channel)
      unreadByChannel[channel] = bumpedMeta?.unread ?? unreadByChannel[channel]
      const totalUnread = Object.values(unreadByChannel).reduce((sum, n) => sum + (Number(n) || 0), 0)

      // "Latest wins" contact-level preview/updated_at — same rule
      // toUnifiedContacts() itself uses when grouping raw rows into a
      // contact, so a message on the contact's currently-primary channel
      // (or simply the most recent one seen) is what surfaces in the list
      // row.
      const convTs = conv.updated_at ? new Date(conv.updated_at).getTime() : 0
      const msgTs = new Date(timestamp).getTime()
      const isPrimaryChannel = conv.activeChannel ? normalizeChannelKey(conv.activeChannel) === channel : true
      const shouldBumpContactLevel = isPrimaryChannel || !conv.updated_at || msgTs >= convTs

      return {
        ...conv,
        channelMeta: nextChannelMeta,
        unreadByChannel,
        unread: totalUnread,
        ...(shouldBumpContactLevel ? { preview, updated_at: timestamp, time: relativeTime(timestamp, t) } : {}),
      }
    })

    return changed ? next : conversations
  }
}

function byUpdatedAtDesc(a, b) {
  const aTs = a?.updated_at ? new Date(a.updated_at).getTime() : 0
  const bTs = b?.updated_at ? new Date(b.updated_at).getTime() : 0
  return bTs - aTs
}

// B-FE4 (Track B2, pagination-and-ws-tenant-wide) — merge logic for the new
// tenant-wide `conversation_list.upsert` WS event
// (growchat-doc/realtime-inbox/spec-inbox-conversation-list-pagination.md).
// Unlike createApplyWsMessageToConversationList() above, this handler:
//   - matches by `contact_id` against the unified-row cache (same identity
//     key toUnifiedContacts() groups by) — "already in cache" means this
//     contact already has a row, not any particular field literally named
//     `id`/`conversation_id`;
//   - INSERTS a brand-new unified row at the front when the contact isn't
//     in the cache yet — the whole point of the tenant-wide topic is
//     surfacing conversations this tab never opened/loaded, which the old
//     per-thread `conversation.message` topic structurally can't do (it's
//     only subscribed to one thread at a time);
//   - ALWAYS re-sorts the full list by updated_at desc afterwards, patch or
//     insert alike, since nothing else keeps sidebar order correct anymore
//     post-Phase-2 (no poll).
//
// Payload field names are this handler's own choice (spec left them open —
// see spec's Open Question #1 — BE ships in parallel, not done yet):
//   conversation_id, contact_id, tenant_id, channel, contact_name,
//   avatar_url, last_message_preview, updated_at, unread_count.
// `unread_count` is treated as the channel's authoritative absolute count
// (mirrors ConversationSummaryOut.unread_count, which toUnifiedContacts()
// already reads the same way from REST) rather than a delta to add —
// simplest contract for the BE side (it already has the post-bump count
// after append_message()) and avoids this handler needing to guess a
// message role it isn't given. The one exception: if the event is for the
// thread this tab currently has open, unread is forced to 0 regardless of
// what the server counted, matching the rest of the app's "an open thread
// reads as read" behavior.
//
// Fields the event does NOT carry (tags, assigned_staff_*, slots, phone,
// ...) default per the same shape createApplyWsMessageToConversationList's
// row objects use elsewhere in this codebase (see InboxPage.jsx's
// toUnifiedContacts()) — empty/null, self-correcting once the row is
// loaded for real via a REST page fetch or the thread is opened.
//
// @param {object} deps
// @param {(iso: string, t: Function) => string} deps.relativeTime
// @param {(channel: string) => string|null} deps.normalizeChannelKey
export function createApplyConversationListUpsert({ relativeTime, normalizeChannelKey }) {
  function buildChannelMetaEntry({ channel, event, unread, updatedAt, preview }) {
    return {
      channel,
      conversation_id: event.conversation_id || null,
      user_id: event.user_id || null,
      unread,
      updated_at: updatedAt,
      mode: 'ai',
      stage: null,
      booking_id: null,
      tags: [],
      important: false,
      slots: {},
      phone: null,
      messages: [],
      preview,
      assigned_staff_id: null,
      assigned_staff_name: null,
      assigned_staff_email: null,
      assigned_at: null,
      channel_bot_enabled: true,
      ai_toggle_disabled: false,
      handed_off: false,
    }
  }

  return function applyConversationListUpsert(conversations, event, { t, activeContactId, activeChannel } = {}) {
    if (!Array.isArray(conversations) || !event) return conversations
    const contactId = event.contact_id
    const channel = normalizeChannelKey(event.channel)
    if (!contactId || !channel) return conversations

    const updatedAt = event.updated_at || new Date().toISOString()
    const preview = event.last_message_preview || ''
    const isActiveThread = activeContactId === contactId && normalizeChannelKey(activeChannel) === channel
    const unread = isActiveThread ? 0 : (Number(event.unread_count) || 0)

    const existingIndex = conversations.findIndex((conv) => conv.contact_id === contactId)

    if (existingIndex === -1) {
      const channelMetaEntry = buildChannelMetaEntry({ channel, event, unread, updatedAt, preview })
      const newRow = {
        id: `contact:${contactId}`,
        contact_id: contactId,
        channels: [channel],
        channelMeta: [channelMetaEntry],
        name: event.contact_name || (t ? t('common.unknown') : ''),
        avatar_url: event.avatar_url || null,
        phone: null,
        email: null,
        address: null,
        gender: null,
        dob: null,
        slots: {},
        preview,
        updated_at: updatedAt,
        mode: 'ai',
        unread,
        unreadByChannel: { [channel]: unread },
        stage: null,
        booking_id: null,
        notes: null,
        tags: [],
        assignedTags: [],
        important: false,
        assigned_staff_id: null,
        assigned_staff_name: null,
        assigned_staff_email: null,
        assigned_at: null,
        time: relativeTime(updatedAt, t),
        activeChannel: channel,
      }
      return [newRow, ...conversations].sort(byUpdatedAtDesc)
    }

    const next = conversations.map((conv, idx) => {
      if (idx !== existingIndex) return conv

      const channelMeta = Array.isArray(conv.channelMeta) ? conv.channelMeta : []
      const hasChannel = channelMeta.some((meta) => normalizeChannelKey(meta.channel) === channel)
      const nextChannelMeta = hasChannel
        ? channelMeta.map((meta) =>
            normalizeChannelKey(meta.channel) === channel
              ? { ...meta, preview, updated_at: updatedAt, unread }
              : meta
          )
        : [...channelMeta, buildChannelMetaEntry({ channel, event, unread, updatedAt, preview })]

      const unreadByChannel = { ...conv.unreadByChannel, [channel]: unread }
      const totalUnread = Object.values(unreadByChannel).reduce((sum, n) => sum + (Number(n) || 0), 0)
      const channels = hasChannel ? conv.channels : [...(conv.channels || []), channel]

      const convTs = conv.updated_at ? new Date(conv.updated_at).getTime() : 0
      const evtTs = new Date(updatedAt).getTime()
      const shouldBumpContactLevel = !conv.updated_at || evtTs >= convTs

      return {
        ...conv,
        channels,
        channelMeta: nextChannelMeta,
        unreadByChannel,
        unread: totalUnread,
        name: conv.name || event.contact_name || conv.name,
        avatar_url: conv.avatar_url ?? (event.avatar_url || null),
        ...(shouldBumpContactLevel
          ? { preview, updated_at: updatedAt, time: relativeTime(updatedAt, t), activeChannel: channel }
          : {}),
      }
    })

    return next.sort(byUpdatedAtDesc)
  }
}

// B-FE8 (Track B2) — drop duplicate rows by contact_id (the unified-row
// identity key toUnifiedContacts() groups by), keeping the FIRST
// occurrence. Shared by InboxPage.jsx for two spots: flattening infinite-
// query pages for render (guards against the same contact's conversation
// landing in two fetched pages — see Edge Cases in the spec), and
// patchConversationCaches() collapsing pages after a local cache mutation.
// First-occurrence-wins so an already-resorted/WS-upserted row pinned near
// the front always beats a stale duplicate surfacing in a later-fetched
// page.
export function dedupeConversationsById(conversations) {
  if (!Array.isArray(conversations)) return conversations
  const seen = new Set()
  const result = []
  for (const conv of conversations) {
    const key = conv?.contact_id ?? conv?.id
    if (key === undefined || key === null) {
      result.push(conv)
      continue
    }
    if (seen.has(key)) continue
    seen.add(key)
    result.push(conv)
  }
  return result
}
